// Pure arrow-routing helpers — geometry only, no React state.

import type { Box, Arrow, Point } from '../types/index';

export const BRIDGE_R = 8;

export function getConnPt(box: Box, side: string): Point {
  if (side === 'left')  return { x: box.x,                 y: box.y + box.height / 2 };
  if (side === 'right') return { x: box.x + box.width,     y: box.y + box.height / 2 };
  if (side === 'top')   return { x: box.x + box.width / 2, y: box.y };
  return                       { x: box.x + box.width / 2, y: box.y + box.height };
}

/** Checks whether adding an arrow from fromId to toId would create a cycle. Uses DFS:
 *  if fromId is reachable starting from toId (walking existing arrows forward), adding
 *  the new arrow would close a loop. */
export function wouldCreateCycle(fromId: number, toId: number, currentArrows: Arrow[]): boolean {
  const visited = new Set<number>();
  const stack = [toId];
  while (stack.length > 0) {
    const current = stack.pop()!;
    if (current === fromId) return true;
    if (visited.has(current)) continue;
    visited.add(current);
    currentArrows
      .filter(a => a.fromBox === current)
      .forEach(a => stack.push(a.toBox));
  }
  return false;
}

// boxesById: optional pre-built lookup. Callers that need waypoints for many arrows
// against the same box list (e.g. CanvasArrow's crossing-detection, which calls this
// once per OTHER arrow) should build the Map once and pass it, instead of this
// function doing its own O(n) find() on every call — that turns an O(arrows² × boxes)
// pass into O(arrows² + boxes), which matters once a flowchart has 100+ of each.
export function getArrowWaypoints(a: Arrow, boxes: Box[], boxesById?: Map<number, Box>): Point[] {
  const lookup = boxesById ?? new Map(boxes.map(b => [b.id, b]));
  const fb = lookup.get(a.fromBox);
  const tb = lookup.get(a.toBox);
  if (!fb || !tb) return [];
  const fp = getConnPt(fb, a.fromSide);
  const tp = getConnPt(tb, a.toSide);
  if (fb.type === 'callout' || tb.type === 'callout') return [fp, tp];
  if (a.fromSide === 'left' || a.fromSide === 'right') {
    if (a.toSide === 'left' || a.toSide === 'right') {
      // Boxes vertically aligned (fp.y === tp.y): a straight line, no bridge/arc needed.
      // Otherwise the middle two waypoints would collapse into a segment of
      // length 0 — a fake bend point that can confuse crossing detection
      // (a real crossing that happens to fall close to that fake point would then
      // incorrectly get skipped as "too close to a bend", so no bridge/arc).
      if (fp.y === tp.y) return [fp, tp];
      const midX = (fp.x + tp.x) / 2;
      return [fp, { x: midX, y: fp.y }, { x: midX, y: tp.y }, tp];
    } else {
      return [fp, { x: tp.x, y: fp.y }, tp];
    }
  } else {
    if (a.toSide === 'top' || a.toSide === 'bottom') {
      // Same degeneracy fix as above, now for horizontally aligned boxes.
      if (fp.x === tp.x) return [fp, tp];
      const midY = (fp.y + tp.y) / 2;
      return [fp, { x: fp.x, y: midY }, { x: tp.x, y: midY }, tp];
    } else {
      return [fp, { x: fp.x, y: tp.y }, tp];
    }
  }
}

export function segCrossing(
  x1: number, y1: number, x2: number, y2: number,
  x3: number, y3: number, x4: number, y4: number,
): { x: number; y: number; t: number } | null {
  const d1x = x2 - x1, d1y = y2 - y1;
  const d2x = x4 - x3, d2y = y4 - y3;
  const denom = d1x * d2y - d1y * d2x;
  if (Math.abs(denom) < 0.001) return null; // parallel
  const t = ((x3 - x1) * d2y - (y3 - y1) * d2x) / denom;
  const u = ((x3 - x1) * d1y - (y3 - y1) * d1x) / denom;
  if (t > 0.01 && t < 0.99 && u > 0.01 && u < 0.99) {
    return { x: x1 + t * d1x, y: y1 + t * d1y, t };
  }
  return null;
}

export function buildBridgedPath(
  wpts: Point[],
  crossings: { segIndex: number; t: number; x: number; y: number }[],
): string {
  if (wpts.length < 2) return '';
  let d = `M ${wpts[0].x} ${wpts[0].y}`;
  for (let i = 0; i < wpts.length - 1; i++) {
    const p1 = wpts[i], p2 = wpts[i + 1];
    const hits = crossings.filter(c => c.segIndex === i).sort((a, b) => a.t - b.t);
    if (hits.length === 0) {
      d += ` L ${p2.x} ${p2.y}`;
    } else {
      const dx = p2.x - p1.x, dy = p2.y - p1.y;
      const len = Math.sqrt(dx * dx + dy * dy);
      if (len < 0.001) { d += ` L ${p2.x} ${p2.y}`; continue; }
      const ux = dx / len, uy = dy / len;
      const dedupedHits: typeof hits = [];
      for (const c of hits) {
        const prev = dedupedHits[dedupedHits.length - 1];
        if (!prev || Math.sqrt((c.x - prev.x) ** 2 + (c.y - prev.y) ** 2) >= BRIDGE_R * 2) {
          dedupedHits.push(c);
        }
      }
      for (const c of dedupedHits) {
        const bx1 = c.x - ux * BRIDGE_R, by1 = c.y - uy * BRIDGE_R;
        const bx2 = c.x + ux * BRIDGE_R, by2 = c.y + uy * BRIDGE_R;
        d += ` L ${bx1} ${by1} A ${BRIDGE_R} ${BRIDGE_R} 0 0 1 ${bx2} ${by2}`;
      }
      d += ` L ${p2.x} ${p2.y}`;
    }
  }
  return d;
}
